import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  Badge,
  Button,
  TabGroup,
  EmptyState,
  Muted,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface Insight {
  id: string;
  type: "velocity" | "trend" | "anomaly" | "suggestion";
  priority: "low" | "medium" | "high" | "critical";
  title: string;
  description: string;
  read: boolean;
  created_at: string;
}

interface Forecast {
  date: string;
  predicted_sales: number;
  low: number;
  high: number;
  reasoning: string;
  events: string[];
}

interface Digest {
  id: string;
  date: string;
  insight_count: number;
  content: string;
}

type Tab = "insights" | "forecasts" | "digests";

const TYPE_VARIANTS: Record<string, "blue" | "gold" | "danger" | "green"> = {
  velocity: "blue",
  trend: "gold",
  anomaly: "danger",
  suggestion: "green",
};

const PRIORITY_VARIANTS: Record<string, "default" | "gold" | "danger" | "purple"> = {
  low: "default",
  medium: "gold",
  high: "purple",
  critical: "danger",
};

// ============================================================
// COMPONENT
// ============================================================

export default function AIInsightsPage() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const [tab, setTab] = useState<Tab>("insights");
  const [insights, setInsights] = useState<Insight[]>([]);
  const [forecasts, setForecasts] = useState<Forecast[]>([]);
  const [digests, setDigests] = useState<Digest[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedDigests, setExpandedDigests] = useState<Set<string>>(new Set());

  // ---- Data fetching ----

  const fetchInsights = useCallback(async () => {
    try {
      const res = await api.get("/ai/insights", { params: { limit: 30 } });
      setInsights(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchForecasts = useCallback(async () => {
    try {
      const res = await api.get("/ai/forecasts", { params: { days: 14 } });
      setForecasts(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchDigests = useCallback(async () => {
    try {
      const res = await api.get("/ai/digests");
      setDigests(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (tab === "insights") fetchInsights();
    if (tab === "forecasts") fetchForecasts();
    if (tab === "digests") fetchDigests();
  }, [tab, fetchInsights, fetchForecasts, fetchDigests]);

  // ---- Actions ----

  const markRead = async (id: string) => {
    try {
      await api.post(`/ai/insights/${id}/read`);
      setInsights((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/ai/insights/read-all");
      setInsights((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch {
      /* ignore */
    }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      await api.post("/ai/analyze");
      await fetchInsights();
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const generateForecasts = async () => {
    setLoading(true);
    try {
      await api.post("/ai/forecasts/generate", null, { params: { days: 7 } });
      await fetchForecasts();
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const toggleDigest = (id: string) => {
    setExpandedDigests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Derived ----

  const unreadCount = insights.filter((i) => !i.read).length;

  // ---- Render helpers ----

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return d;
    }
  };

  const formatDateTime = (d: string) => {
    try {
      return new Date(d).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return d;
    }
  };

  const formatCurrency = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  // ---- Tabs ----

  const tabs = [
    { key: "insights", label: `Insights${unreadCount > 0 ? ` (${unreadCount})` : ""}` },
    { key: "forecasts", label: "Forecasts" },
    { key: "digests", label: "Digests" },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="AI Insights & Forecasts"
        subtitle="Data-driven analysis, predictions, and daily digests"
      />

      <TabGroup tabs={tabs} active={tab} onChange={(k) => setTab(k as Tab)} />

      {/* ============================================================ */}
      {/* INSIGHTS TAB */}
      {/* ============================================================ */}
      {tab === "insights" && (
        <div>
          {/* Action bar */}
          <div className="flex items-center gap-3 mb-6">
            {unreadCount > 0 && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold"
                style={{ background: "#C5303015", color: "var(--rbw-danger)", border: "1px solid #C5303040" }}
              >
                {unreadCount} unread
              </div>
            )}
            <div className="flex-1" />
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead}>
                Mark All Read
              </Button>
            )}
            {isManager && (
              <Button variant="secondary" size="sm" onClick={runAnalysis} disabled={loading}>
                {loading ? "Analyzing..." : "Run Analysis"}
              </Button>
            )}
          </div>

          {insights.length === 0 ? (
            <EmptyState
              icon="💡"
              message="No insights yet"
              detail="Run an analysis to generate AI-powered insights about your sales data."
            />
          ) : (
            <div className="space-y-3">
              {insights.map((insight) => (
                <Card
                  key={insight.id}
                  className={`transition-all ${!insight.read ? "ring-2" : ""}`}
                  padding="p-5"
                >
                  <div
                    className="flex items-start gap-4 cursor-pointer"
                    onClick={() => !insight.read && markRead(insight.id)}
                    style={
                      !insight.read
                        ? { borderLeft: "none" }
                        : undefined
                    }
                  >
                    {/* Unread dot */}
                    <div className="pt-1 flex-shrink-0">
                      {!insight.read && (
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ background: "var(--rbw-blue)" }}
                        />
                      )}
                      {insight.read && <div className="w-2.5" />}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Badge variant={TYPE_VARIANTS[insight.type]}>{insight.type}</Badge>
                        <Badge variant={PRIORITY_VARIANTS[insight.priority]}>{insight.priority}</Badge>
                        <Muted>
                          <span className="text-xs">{formatDateTime(insight.created_at)}</span>
                        </Muted>
                      </div>
                      <div
                        className="text-sm font-semibold mb-1"
                        style={{ color: "var(--rbw-navy)" }}
                      >
                        {insight.title}
                      </div>
                      <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                        {insight.description}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* FORECASTS TAB */}
      {/* ============================================================ */}
      {tab === "forecasts" && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1" />
            <Button variant="secondary" size="sm" onClick={generateForecasts} disabled={loading}>
              {loading ? "Generating..." : "Generate Forecasts"}
            </Button>
          </div>

          {forecasts.length === 0 ? (
            <EmptyState
              icon="📊"
              message="No forecasts available"
              detail="Generate forecasts to see predicted daily sales for the upcoming week."
            />
          ) : (
            <div className="space-y-3">
              {forecasts.map((fc) => {
                const range = fc.high - fc.low;
                const predictedPct = range > 0 ? ((fc.predicted_sales - fc.low) / range) * 100 : 50;

                return (
                  <Card key={fc.date} padding="p-5">
                    <div className="flex items-start gap-5">
                      {/* Date */}
                      <div className="flex-shrink-0 w-20 text-center">
                        <div className="text-xs font-semibold uppercase" style={{ color: "var(--rbw-gray-dark)" }}>
                          {formatDate(fc.date)}
                        </div>
                      </div>

                      {/* Range indicator */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium" style={{ color: "var(--rbw-gray-dark)" }}>
                            {formatCurrency(fc.low)}
                          </span>
                          <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                            {formatCurrency(fc.predicted_sales)}
                          </span>
                          <span className="text-xs font-medium" style={{ color: "var(--rbw-gray-dark)" }}>
                            {formatCurrency(fc.high)}
                          </span>
                        </div>

                        {/* Visual bar */}
                        <div
                          className="relative h-3 rounded-full overflow-hidden"
                          style={{ background: "var(--rbw-gray-light)" }}
                        >
                          <div
                            className="absolute inset-y-0 rounded-full"
                            style={{
                              left: "0%",
                              right: "0%",
                              background: "#8BB8E830",
                            }}
                          />
                          <div
                            className="absolute top-0 bottom-0 w-3 h-3 rounded-full"
                            style={{
                              left: `calc(${Math.min(Math.max(predictedPct, 5), 95)}% - 6px)`,
                              background: "var(--rbw-blue)",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                            }}
                          />
                        </div>

                        {/* Events */}
                        {fc.events && fc.events.length > 0 && (
                          <div className="flex gap-1.5 mt-2 flex-wrap">
                            {fc.events.map((evt, i) => (
                              <Badge key={i} variant="purple">{evt}</Badge>
                            ))}
                          </div>
                        )}

                        {/* Reasoning */}
                        {fc.reasoning && (
                          <div className="text-xs mt-2" style={{ color: "var(--rbw-gray-dark)" }}>
                            {fc.reasoning}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* DIGESTS TAB */}
      {/* ============================================================ */}
      {tab === "digests" && (
        <div>
          {digests.length === 0 ? (
            <EmptyState
              icon="📋"
              message="No digests yet"
              detail="Daily digests are generated automatically and will appear here."
            />
          ) : (
            <div className="space-y-3">
              {digests.map((digest) => {
                const isExpanded = expandedDigests.has(digest.id);
                return (
                  <Card key={digest.id} padding="p-0">
                    <button
                      className="w-full flex items-center justify-between p-5 text-left"
                      onClick={() => toggleDigest(digest.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                          {formatDate(digest.date)}
                        </div>
                        <Badge variant="blue">{digest.insight_count} insights</Badge>
                      </div>
                      <svg
                        className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        style={{ color: "var(--rbw-gray-dark)" }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isExpanded && (
                      <div
                        className="px-5 pb-5 text-sm whitespace-pre-wrap"
                        style={{ color: "var(--rbw-gray-dark)", borderTop: "1px solid var(--rbw-border)" }}
                      >
                        <div className="pt-4">{digest.content}</div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
